import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * A (temporary) userland implementation of `useEvent` from this React RFC: https://github.com/reactjs/rfcs/pull/220.
 *
 * Typically useful for getting a stable reference to an "event" function (i.e. one that isn't called during render
 * but is called later in a useEffect or event handler) that accesses state or props.
 */
export function useEvent<T extends unknown[], U>(handler: (...args: T) => U): (...args: T) => U {
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  return useCallback((...args) => handlerRef.current(...args), []);
}
